// Helper functions for search operations

/**
 * Search cities function
 * @param {string} query - The search query
 * @param {Function} setCitySearchResults - State setter for city search results
 * @param {Function} setShowCitySearchResults - State setter for showing city search results
 * @param {Function} setLoading - State setter for loading state
 * @param {Function} setErrors - State setter for errors
 */
export const searchCities = async (query, setCitySearchResults, setShowCitySearchResults, setLoading, setErrors) => {
  if (!query.trim()) {
    setCitySearchResults([]);
    setShowCitySearchResults(false);
    return;
  }
  
  setLoading(prev => ({ ...prev, search: true }));
  setErrors(prev => ({ ...prev, search: null }));
  
  try {
    const token = localStorage.getItem('authToken'); // Use authToken like in api.js
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/cities/search?query=${encodeURIComponent(query)}&page=1&limit=20`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const result = await response.json();
    
    if (result.success) {
      setCitySearchResults(result.data.cities || []);
      setShowCitySearchResults(true);
    } else {
      setErrors(prev => ({ ...prev, search: result.message || 'Failed to search cities' }));
    }
  } catch (error) {
    console.error('Search error:', error);
    setErrors(prev => ({ ...prev, search: 'Network error occurred while searching cities' }));
  } finally {
    setLoading(prev => ({ ...prev, search: false }));
  }
};

/**
 * Search hotels function
 * @param {string} cityId - The city ID to search in
 * @param {string} countryCode - The country code
 * @param {Function} setSearchResults - State setter for search results
 * @param {Function} setShowSearchResults - State setter for showing search results
 * @param {Function} setShowCitySearchResults - State setter for showing city search results
 * @param {Function} setLoading - State setter for loading state
 * @param {Function} setErrors - State setter for errors
 * @param {string} TRAVEL_API_BASE - The API base URL
 */
export const searchHotels = async (cityId, countryCode, setSearchResults, setShowSearchResults, setShowCitySearchResults, setLoading, setErrors, TRAVEL_API_BASE, checkInDate, checkOutDate) => {
  if (!cityId) return;
  
  setLoading(prev => ({ ...prev, search: true }));
  setErrors(prev => ({ ...prev, search: null }));
  
  try {
    const token = localStorage.getItem('authToken'); // Use authToken like in api.js
    
    // Use provided dates or fallback to default dates
    let selectedCheckInDate = checkInDate;
    let selectedCheckOutDate = checkOutDate;
    
    if (!selectedCheckInDate || !selectedCheckOutDate) {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const checkOut = new Date(tomorrow);
      checkOut.setDate(checkOut.getDate() + 1); // 1 night stay
      
      selectedCheckInDate = tomorrow;
      selectedCheckOutDate = checkOut;
    }
    
    const formatDate = (date) => {
      // Format date as YYYY-MM-DD without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const requestBody = {
      CheckInDate: formatDate(selectedCheckInDate),
      CheckOutDate: formatDate(selectedCheckOutDate),
      CityId: cityId,
      GuestNationality: countryCode,
      NoOfRooms: "1",
      NoOfNights: "1",
      RoomGuests: [
        {
          NoOfAdults: "1",
          NoOfChild: "0"
        }
      ]
    };
    
    const response = await fetch(`${TRAVEL_API_BASE}/hotels/search`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    
    const result = await response.json();
    
    if (result.success) {
      const hotelResults = result.data?.Search?.HotelSearchResult?.HotelResults || [];
      setSearchResults(hotelResults);
      setShowSearchResults(true);
      setShowCitySearchResults(false);
    } else {
      setErrors(prev => ({ ...prev, search: result.message || 'Failed to search hotels' }));
    }
  } catch (error) {
    console.error('Search error:', error);
    setErrors(prev => ({ ...prev, search: 'Network error occurred while searching hotels' }));
  } finally {
    setLoading(prev => ({ ...prev, search: false }));
  }
};

/**
 * Search rooms function for a specific hotel
 * @param {Object} hotel - The hotel object
 * @param {Function} setRoomSearchLoading - State setter for room search loading
 * @param {Function} setRoomSearchError - State setter for room search error
 * @param {Function} setSelectedHotelForRoomSearch - State setter for selected hotel for room search
 * @param {Function} setRoomSearchResults - State setter for room search results
 * @param {Function} setShowRoomSearchResults - State setter for showing room search results
 * @param {string} TRAVEL_API_BASE - The API base URL
 * @param {Object} travelomatixRoomsAPI - The travelomatix rooms API object
 */
export const searchRooms = async (hotel, setRoomSearchLoading, setRoomSearchError, setSelectedHotelForRoomSearch, setRoomSearchResults, setShowRoomSearchResults, TRAVEL_API_BASE, travelomatixRoomsAPI) => {
  setRoomSearchLoading(true);
  setRoomSearchError(null);
  setSelectedHotelForRoomSearch(hotel);
  
  try {
    // First, search for cities to get the city code
    const token = localStorage.getItem('authToken');
    
    // Search for the city of the hotel
    const cityResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api'}/cities/search?query=${encodeURIComponent(hotel.city)}&page=1&limit=20`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    const cityResult = await cityResponse.json();
    
    if (!cityResult.success || !cityResult.data.cities || cityResult.data.cities.length === 0) {
      throw new Error('Could not find city for the hotel');
    }
    
    // Get the first matching city
    const city = cityResult.data.cities[0];
    
    // Search for hotels in that city
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const checkOut = new Date(tomorrow);
    checkOut.setDate(checkOut.getDate() + 1); // 1 night stay
    
    const formatDate = (date) => {
      // Format date as YYYY-MM-DD without timezone conversion
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const hotelSearchBody = {
      CheckInDate: formatDate(tomorrow),
      CheckOutDate: formatDate(checkOut),
      CityId: city.city_code,
      GuestNationality: "SA", // Default nationality
      NoOfRooms: "1",
      NoOfNights: "1",
      RoomGuests: [
        {
          NoOfAdults: "2", // Default to 2 adults
          NoOfChild: "0"
        }
      ]
    };
    
    const hotelResponse = await fetch(
      `${TRAVEL_API_BASE}/hotels/search`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(hotelSearchBody)
      }
    );
    
    const hotelResult = await hotelResponse.json();
    
    if (!hotelResult.success) {
      throw new Error(hotelResult.message || 'Failed to search hotels');
    }
    
    const hotelResults = hotelResult.data?.Search?.HotelSearchResult?.HotelResults || [];
    
    // Find the matching hotel by supplier_id (HOTEL_CODE)
    const matchingHotel = hotelResults.find(h => h.HotelCode === hotel.supplier_id);
    
    if (!matchingHotel) {
      throw new Error('Could not find matching hotel in Travelomatix');
    }
    
    // Now search for rooms using the ResultToken from the matching hotel
    const roomSearchBody = {
      ResultToken: matchingHotel.ResultToken,
      CheckInDate: formatDate(tomorrow),
      CheckOutDate: formatDate(checkOut),
      GuestNationality: "SA",
      NoOfRooms: 1,
      NoOfNights: 1,
      RoomGuests: [
        {
          NoOfAdults: 2,
          NoOfChild: 0
        }
      ]
    };
    
    const roomResponse = await travelomatixRoomsAPI.search(roomSearchBody);
    
    if (roomResponse.data.success) {
      const roomResults = roomResponse.data?.data?.RoomList?.GetHotelRoomResult?.HotelRoomsDetails || [];
      setRoomSearchResults(roomResults);
      setShowRoomSearchResults(true);
    } else {
      setRoomSearchError(roomResponse.data.message || 'Failed to search rooms');
    }
  } catch (error) {
    console.error('Room search error:', error);
    setRoomSearchError(error.message || 'Network error occurred while searching rooms');
  } finally {
    setRoomSearchLoading(false);
  }
};

export default {
  searchCities,
  searchHotels,
  searchRooms
};